import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST, IsImpersonationMode } from 'config/config';
import useToast from 'services/Hooks/useToast';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

export const usePrefetchCart = (isLogged, dealerId) => {
  const queryClient = useQueryClient();

  queryClient.prefetchQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const shouldSendDealerId = IsImpersonationMode && !!dealerId;
      const response = await axios.get(
        HOST + '/api/dealer-portal/cart/active',
        shouldSendDealerId ? { params: { dealerId: dealerId } } : undefined,
      );
      return response.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!isLogged,
  });
};

export const useCarts = tableDataParam => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['carts', JSON.stringify(tableDataParam)],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/cart', {
        paramsSerializer: params => tableDataQueryStringBuilder(params),
        params: tableDataParam,
      });
      return response.data;
    },
    throwOnError: errorMessage,
    staleTime: 0,
  });
  return query;
};

export const useActiveCart = (isLogged, forcedFetch = false, dealerId = null) => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const shouldSendDealerId = IsImpersonationMode && !!dealerId;
      const response = await axios.get(
        HOST + '/api/dealer-portal/cart/active',
        shouldSendDealerId ? { params: { dealerId: dealerId } } : undefined,
      );
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
    staleTime: forcedFetch ? 0 : Infinity,
  });
  return query;
};

export const useCart = cartId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['historyCart', cartId],
    queryFn: async () => {
      if (cartId) {
        const response = await axios.get(`${HOST}/api/dealer-portal/cart/${cartId}`);
        return response.data;
      } else {
        return null;
      }
    },
    throwOnError: errorMessage,
  });
  return query;
};

export const usePostCarItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();

  const query = useMutation({
    mutationKey: ['addCartItem'],
    mutationFn: async cartItem => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/items`, isDemo ? { ...cartItem, isDemo: true } : cartItem);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCartWall = isDemo => {
  const { saveMessage, errorMessage } = useToast();

  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addCartWall'],
    mutationFn: async cartWall => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/walls`, isDemo ? { ...cartWall, isDemo: true } : cartWall);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCartTooling = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addCartTooling'],
    mutationFn: async cartTooling => {
      return await axios.post(
        `${HOST}/api/dealer-portal/cart/toolings`,
        isDemo ? { ...cartTooling, isDemo: true } : cartTooling,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostCheckout = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addCartItem'],
    mutationFn: async cart => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/${cart.id}/checkout`);
    },
    onSuccess: () => {
      saveMessage();
      queryClient.setQueryData(['cart'], null);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeCartItemQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityCartItem'],
    mutationFn: async cartItem => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/items/${cartItem.id}/quantity`, cartItem);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeCartToolingQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityCartTooling'],
    mutationFn: async cartTooling => {
      return await axios.post(`${HOST}/api/dealer-portal/cart/toolings/${cartTooling.id}/quantity`, cartTooling);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApplyPromoCode = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['applyPromoCode'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/promo`, cart);
    },
    onSuccess: ({ data }) => {
      if (data.message) {
        errorMessage(data);
      } else {
        saveMessage();
      }
      queryClient.setQueryData(['cart'], data.cart);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchChangeShipping = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeShipping'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/shipping`, cart);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApproveCart = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['approveCart'],
    mutationFn: async cart => {
      return await axios.patch(`${HOST}/api/dealer-portal/cart/${cart.id}/status`, cart);
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      queryClient.setQueryData(['historyCart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCartItem = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCartItem'],
    mutationFn: async cartItem => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/items/${cartItem.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCartWall = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCartWall'],
    mutationFn: async cartWall => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/walls/${cartWall.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCartTooling = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCartWall'],
    mutationFn: async cartTooling => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/toolings/${cartTooling.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['cart'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteCart = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteCart'],
    mutationFn: async cart => {
      return await axios.delete(`${HOST}/api/dealer-portal/cart/${cart.id}`);
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.setQueryData(['cart'], null);
    },
    onError: errorMessage,
  });
  return query;
};
